// components/customers/BatchCheckDialog.tsx
import { useMemo, useState } from 'react'
import { Search, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { useCheckContactBatch } from '@/hooks/useCustomers'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { ContactType, CheckContactBatchResponse } from '@/types/customer'
import { checkContactBatchSchema } from '@/schemas/customer'
import { showError } from '@/lib/error'

const MAX_BATCH_CHECK_COUNT = 20000

interface BatchCheckDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: ContactType // 1-手机号, 2-微信号
}

export function BatchCheckDialog({ open, onOpenChange, type }: BatchCheckDialogProps) {
  const [inputValue, setInputValue] = useState('')
  const [results, setResults] = useState<CheckContactBatchResponse | null>(null)

  const checkMutation = useCheckContactBatch()

  const title = type === 1 ? '手机号批量检查' : '微信号批量检查'
  const placeholder = type === 1
    ? '批量输入手机号，每行一个或逗号分隔（最多 20000 个）'
    : '批量输入微信号，每行一个或逗号分隔（最多 20000 个）'

  // 解析并去重输入值
  const values = useMemo(() => {
    const seen = new Set<string>()
    return inputValue
      .split(/[,\n]/)
      .map(v => v.trim())
      .filter(v => v.length > 0)
      .filter(v => {
        if (seen.has(v)) return false
        seen.add(v)
        return true
      })
  }, [inputValue])

  const count = values.length
  const isOverLimit = count > MAX_BATCH_CHECK_COUNT
  const canCheck = count > 0 && !isOverLimit && !checkMutation.isPending

  const handleCheck = async () => {
    const parsed = checkContactBatchSchema.safeParse({ type, values })
    if (!parsed.success) {
      const message = parsed.error.issues[0]?.message || '输入不合法'
      showError(new Error(message), '批量检查失败')
      return
    }

    try {
      const result = await checkMutation.mutateAsync(parsed.data)
      setResults(result)
    } catch (error) {
      showError(error, '批量检查失败')
    }
  }

  const handleClose = () => {
    setInputValue('')
    setResults(null)
    onOpenChange(false)
  }

  // 统计结果
  const stats = results
    ? {
        total: Object.keys(results.results).length,
        exists: Object.values(results.results).filter(r => r.exists).length,
        available: Object.values(results.results).filter(r => !r.exists).length,
      }
    : null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            批量检查{type === 1 ? '手机号' : '微信号'}是否已被锁定
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 输入区域 */}
          <div className="space-y-2">
            <textarea
              placeholder={placeholder}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="flex items-center justify-between text-sm">
              <span className={isOverLimit ? 'text-destructive font-medium' : 'text-muted-foreground'}>
                共 <strong>{count}</strong> 个（最多 {MAX_BATCH_CHECK_COUNT} 个）
              </span>
              {isOverLimit && (
                <span className="flex items-center gap-1 text-destructive text-xs">
                  <AlertCircle className="h-3.5 w-3.5" />
                  超出上限，请减少后重试
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCheck}
                disabled={!canCheck}
                className="flex-1"
              >
                {checkMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                批量检查
              </Button>
              <Button variant="outline" onClick={() => setResults(null)}>
                清空结果
              </Button>
            </div>
          </div>

          {/* 统计信息 */}
          {stats && (
            <div className="flex gap-4 text-sm">
              <span>共 <strong>{stats.total}</strong> 个</span>
              <span className="text-destructive">已锁定: <strong>{stats.exists}</strong></span>
              <span className="text-green-600">可用: <strong>{stats.available}</strong></span>
            </div>
          )}

          {/* 结果列表 */}
          {results && (
            <ScrollArea className="h-[300px] rounded-md border">
              <div className="p-2 space-y-1">
                {Object.entries(results.results).map(([value, result]) => (
                  <div key={value} className="flex items-center justify-between p-2 rounded hover:bg-muted">
                    <div className="flex items-center gap-2">
                      {result.exists ? (
                        <XCircle className="h-4 w-4 text-destructive" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                      <code className="text-sm">{value}</code>
                    </div>
                    <div className="flex items-center gap-2">
                      {result.exists ? (
                        <>
                          <span className="text-sm text-muted-foreground">
                            {result.customer?.name}
                          </span>
                          <Badge variant="destructive" className="text-xs">
                            已锁定
                          </Badge>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                          可用
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
